import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { TrendingUp, TrendingDown, Wallet, RefreshCw, Eye, BarChart3, History, Download, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { toast } from 'sonner';

const BalanceMonitor = () => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [transactions, setTransactions] = useState({ payments: [], withdrawals: [] });
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchBalance();
  }, []);

  useEffect(() => {
    let interval = null;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchBalance();
      }, 30000); // Refresh every 30 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const fetchBalance = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/balance/current`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBalance(data);
      } else {
        throw new Error('Erreur lors du chargement du solde');
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionHistory = async () => {
    setLoadingHistory(true);
    try {
      const [paymentsRes, withdrawalsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/history`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/withdrawals`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (paymentsRes.ok && withdrawalsRes.ok) {
        let payments = await paymentsRes.json();
        const withdrawals = await withdrawalsRes.json();
        // ne conserver que les paiements confirmés pour les entrées valides
        payments = payments.filter(p => p.status && p.status.toLowerCase() === 'confirmed');
        setTransactions({ payments, withdrawals });
      }
    } catch (error) {
      console.error('Error fetching transaction history:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleOpenHistory = () => {
    setShowHistoryDialog(true);
    fetchTransactionHistory();
  };

  const downloadHistoryCSV = () => {
    try {
      // Préparer les données CSV
      let csvContent = "Type,Date,Description,Montant,Devise,Méthode,Statut\n";
      
      // Ajouter les paiements (entrées)
      transactions.payments.forEach(payment => {
        const date = new Date(payment.declared_at).toLocaleDateString('fr-FR');
        const amount = payment.amount || 0;
        const description = `Paiement de ${payment.client_name}`;
        csvContent += `Entrée,${date},"${description}",${amount},${payment.currency},${payment.payment_method},${payment.status}\n`;
      });

      // Ajouter les retraits (sorties)
      transactions.withdrawals.forEach(withdrawal => {
        const date = new Date(withdrawal.withdrawal_date).toLocaleDateString('fr-FR');
        const amount = withdrawal.amount || 0;
        const description = `${withdrawal.category} - ${withdrawal.subcategory}`;
        csvContent += `Sortie,${date},"${description}",${amount},CFA,${withdrawal.description},-\n`;
      });

      // Créer le fichier et télécharger
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `historique_transactions_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('✅ Historique téléchargé avec succès');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount) + ' CFA';
  };

  const getBalanceStatus = () => {
    if (!balance) return { color: 'text-slate-400', bgColor: 'bg-slate-500/20', label: 'Non disponible' };
    
    if (balance.current_balance > 10000) {
      return { color: 'text-green-400', bgColor: 'bg-green-500/20', label: 'Excellent' };
    } else if (balance.current_balance > 5000) {
      return { color: 'text-blue-400', bgColor: 'bg-blue-500/20', label: 'Bon' };
    } else if (balance.current_balance > 1000) {
      return { color: 'text-yellow-400', bgColor: 'bg-yellow-500/20', label: 'Modéré' };
    } else if (balance.current_balance > 0) {
      return { color: 'text-orange-400', bgColor: 'bg-orange-500/20', label: 'Faible' };
    } else {
      return { color: 'text-red-400', bgColor: 'bg-red-500/20', label: 'Attention' };
    }
  };

  const balanceStatus = getBalanceStatus();

  return (
    <div className="space-y-6">
      {/* Real-time Balance Card */}
      <Card className="bg-gradient-to-r from-slate-700 to-slate-800 border-slate-600">
        <CardHeader>
          <div className="flex justify-between items-start flex-wrap gap-3">
            <div>
              <CardTitle className="text-white flex items-center space-x-2">
                <Wallet className="h-6 w-6 text-orange-500" />
                <span>Solde Temps Réel</span>
              </CardTitle>
              <CardDescription className="text-slate-400">
                Solde actualisé automatiquement
              </CardDescription>
            </div>
            <div className="flex items-center space-x-2 flex-wrap gap-2">
              <Button
                onClick={handleOpenHistory}
                variant="outline"
                size="sm"
                className="border-slate-600 text-slate-300 hover:bg-slate-600"
              >
                <History className="h-4 w-4 mr-1" />
                Historique
              </Button>
              <Badge className={balanceStatus.bgColor + ' ' + balanceStatus.color}>
                {balanceStatus.label}
              </Badge>
              <Button
                onClick={() => setAutoRefresh(!autoRefresh)}
                variant="outline"
                size="sm"
                className={`border-slate-600 ${autoRefresh ? 'bg-green-600 text-white' : 'text-slate-300'}`}
              >
                <Eye className="h-4 w-4 mr-1" />
                {autoRefresh ? 'Actif' : 'Manuel'}
              </Button>
              <Button
                onClick={fetchBalance}
                disabled={loading}
                variant="outline"
                size="sm"
                className="border-slate-600 text-slate-300"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {balance ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Current Balance */}
              <div className="text-center">
                <div className="flex justify-center mb-2">
                  <div className={`p-3 rounded-full ${balanceStatus.bgColor}`}>
                    <Wallet className={`h-8 w-8 ${balanceStatus.color}`} />
                  </div>
                </div>
                <h3 className="text-sm font-medium text-slate-400 mb-1">Solde Actuel</h3>
                <p className={`text-2xl font-bold ${balanceStatus.color}`}>
                  {formatCurrency(balance.current_balance)}
                </p>
              </div>

              {/* Total Payments */}
              <div className="text-center">
                <div className="flex justify-center mb-2">
                  <div className="p-3 rounded-full bg-green-500/20">
                    <TrendingUp className="h-8 w-8 text-green-400" />
                  </div>
                </div>
                <h3 className="text-sm font-medium text-slate-400 mb-1">Total Encaissements</h3>
                <p className="text-2xl font-bold text-green-400">
                  {formatCurrency(balance.total_payments)}
                </p>
              </div>

              {/* Total Withdrawals */}
              <div className="text-center">
                <div className="flex justify-center mb-2">
                  <div className="p-3 rounded-full bg-red-500/20">
                    <TrendingDown className="h-8 w-8 text-red-400" />
                  </div>
                </div>
                <h3 className="text-sm font-medium text-slate-400 mb-1">Total Retraits</h3>
                <p className="text-2xl font-bold text-red-400">
                  {formatCurrency(balance.total_withdrawals)}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="animate-pulse">
                <div className="w-16 h-16 bg-slate-600 rounded-full mx-auto mb-4"></div>
                <div className="w-32 h-4 bg-slate-600 rounded mx-auto mb-2"></div>
                <div className="w-24 h-4 bg-slate-600 rounded mx-auto"></div>
              </div>
            </div>
          )}

          {balance && (
            <div className="mt-6 pt-6 border-t border-slate-600">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-400">Dernière mise à jour:</span>
                <span className="text-slate-300">
                  {new Date(balance.last_updated).toLocaleString('fr-FR')}
                </span>
              </div>
              {autoRefresh && (
                <div className="flex justify-center mt-2">
                  <Badge className="bg-green-500/20 text-green-400">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse mr-2"></div>
                    Actualisation automatique (30s)
                  </Badge>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Balance Analytics */}
      <Card className="bg-slate-700 border-slate-600">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <BarChart3 className="h-5 w-5 text-orange-500" />
            <span>Analyse Financière</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {balance && (
            <div className="space-y-4">
              {/* Ratio Analysis */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-600 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-2">Ratio Encaissements/Retraits</h4>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-slate-700 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{
                          width: `${Math.min((balance.total_payments / (balance.total_payments + balance.total_withdrawals)) * 100, 100)}%`
                        }}
                      ></div>
                    </div>
                    <span className="text-green-400 text-sm font-medium">
                      {Math.round((balance.total_payments / (balance.total_payments + balance.total_withdrawals)) * 100)}%
                    </span>
                  </div>
                </div>

                <div className="bg-slate-600 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-2">Marge Opérationnelle</h4>
                  <div className="flex items-center space-x-2">
                    <span className={`text-2xl font-bold ${
                      balance.current_balance > 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {balance.total_payments > 0 ? Math.round((balance.current_balance / balance.total_payments) * 100) : 0}%
                    </span>
                    {balance.current_balance > 0 ? (
                      <TrendingUp className="h-5 w-5 text-green-400" />
                    ) : (
                      <TrendingDown className="h-5 w-5 text-red-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Financial Health Indicators */}
              <div className="bg-slate-600 rounded-lg p-4">
                <h4 className="text-white font-medium mb-3">Indicateurs de Santé Financière</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Liquidité immédiate:</span>
                    <Badge className={balance.current_balance > 5000 ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}>
                      {balance.current_balance > 5000 ? 'Bonne' : 'Attention'}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Capacité de paiement:</span>
                    <Badge className={balance.current_balance > 2000 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                      {balance.current_balance > 2000 ? 'Sécurisée' : 'Risque'}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Rentabilité:</span>
                    <Badge className={balance.current_balance > balance.total_payments * 0.3 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                      {balance.current_balance > balance.total_payments * 0.3 ? 'Excellente' : 'À améliorer'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Historique des Transactions */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <History className="h-6 w-6 text-orange-500" />
              Historique des Transactions
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Tous les mouvements financiers (entrées et sorties)
            </DialogDescription>
          </DialogHeader>
          
          {loadingHistory ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-6 pr-2">
              {/* Résumé */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="bg-slate-700 border-slate-600">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="flex justify-center mb-2">
                        <ArrowUpCircle className="h-8 w-8 text-green-400" />
                      </div>
                      <p className="text-sm text-slate-400">Total Entrées</p>
                      <p className="text-2xl font-bold text-green-400">{transactions.payments.length}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-700 border-slate-600">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="flex justify-center mb-2">
                        <ArrowDownCircle className="h-8 w-8 text-red-400" />
                      </div>
                      <p className="text-sm text-slate-400">Total Sorties</p>
                      <p className="text-2xl font-bold text-red-400">{transactions.withdrawals.length}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-700 border-slate-600">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <div className="flex justify-center mb-2">
                        <BarChart3 className="h-8 w-8 text-blue-400" />
                      </div>
                      <p className="text-sm text-slate-400">Total Transactions</p>
                      <p className="text-2xl font-bold text-blue-400">
                        {transactions.payments.length + transactions.withdrawals.length}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Liste des Entrées */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <ArrowUpCircle className="h-5 w-5 text-green-400" />
                    Entrées d'Argent ({transactions.payments.length})
                  </h3>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {transactions.payments.length > 0 ? (
                    transactions.payments.map((payment, index) => (
                      <div key={index} className="bg-slate-800 rounded-lg p-3 flex items-center justify-between hover:bg-slate-750 transition-colors">
                        <div className="flex-1">
                          <p className="text-white font-medium">{payment.client_name}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-slate-400">
                              {new Date(payment.declared_at).toLocaleDateString('fr-FR')}
                            </span>
                            <Badge className="text-xs bg-blue-500/20 text-blue-400">
                              {payment.payment_method}
                            </Badge>
                            <Badge className={`text-xs ${
                              payment.status === 'confirmed' || payment.status === 'CONFIRMED'
                                ? 'bg-green-500/20 text-green-400'
                                : payment.status === 'pending'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {payment.status}
                            </Badge>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-bold">
                            +{parseFloat(payment.amount || 0).toLocaleString()} {payment.currency}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-400 text-center py-4">Aucune entrée</p>
                  )}
                </div>
              </div>

              {/* Liste des Sorties */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <ArrowDownCircle className="h-5 w-5 text-red-400" />
                    Sorties d'Argent ({transactions.withdrawals.length})
                  </h3>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {transactions.withdrawals.length > 0 ? (
                    transactions.withdrawals.map((withdrawal, index) => (
                      <div key={index} className="bg-slate-800 rounded-lg p-3 flex items-center justify-between hover:bg-slate-750 transition-colors">
                        <div className="flex-1">
                          <p className="text-white font-medium">{withdrawal.category} - {withdrawal.subcategory}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-slate-400">
                              {new Date(withdrawal.withdrawal_date).toLocaleDateString('fr-FR')}
                            </span>
                            <span className="text-xs text-slate-500">{withdrawal.description}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-red-400 font-bold">
                            -{parseFloat(withdrawal.amount || 0).toLocaleString()} CFA
                          </p>
                          <p className="text-xs text-slate-400">{withdrawal.manager_name}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-400 text-center py-4">Aucune sortie</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-700">
            <Button
              onClick={downloadHistoryCSV}
              className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
              disabled={loadingHistory || (transactions.payments.length === 0 && transactions.withdrawals.length === 0)}
            >
              <Download className="h-4 w-4 mr-2" />
              Télécharger l'Historique (CSV)
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowHistoryDialog(false)}
              className="flex-1 border-slate-600 text-white hover:bg-slate-700"
            >
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BalanceMonitor;